import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReceiptLongOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { invoiceService } from "../../services";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "PENDING", label: "Pendiente" },
  { value: "PROCESSING", label: "En proceso" },
  { value: "COMPLETED", label: "Completada" },
  { value: "FAILED", label: "Error" },
  { value: "CANCELLED", label: "Cancelada" },
];

const STATUS_LABELS = {
  PENDING: "Pendiente",
  PROCESSING: "En proceso",
  COMPLETED: "Completada",
  FAILED: "Error",
  CANCELLED: "Cancelada",
};

const STATUS_COLORS = {
  PENDING: "warning",
  PROCESSING: "info",
  COMPLETED: "success",
  FAILED: "error",
  CANCELLED: "default",
};

const initialFilters = {
  search: "",
  status: "",
  date_from: "",
  date_to: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operación.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]) return String(data.detail[0]);
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

function InvoicePendingPage() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [preparingId, setPreparingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const params = useMemo(() => {
    const next = {};
    Object.entries(appliedFilters).forEach(([key, value]) => {
      const normalized = String(value || "").trim();
      if (normalized) next[key] = normalized;
    });
    return next;
  }, [appliedFilters]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoiceService.listPending(params);
      setRows(toArray(data));
    } catch {
      setRows([]);
      setSnack({ open: true, message: "No se pudo cargar la bandeja de pendientes.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const handlePrepareInvoice = async (row) => {
    if (!row?.id || String(row.status || "").toUpperCase() !== "PENDING") return;
    setPreparingId(row.id);
    try {
      await invoiceService.createInternalInvoiceFromPending(row.id);
      setSnack({ open: true, message: "Factura interna preparada.", severity: "success" });
      await loadRows();
    } catch (error) {
      setSnack({
        open: true,
        message: extractErrorMessage(error, "No se pudo preparar la factura interna."),
        severity: "error",
      });
    } finally {
      setPreparingId(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MainText
        title="Pendientes de facturación"
        subtitle="Ventas enviadas a la bandeja para facturarse posteriormente."
        icon={ReceiptLongOutlined}
      />

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
              <TextField
                size="small"
                label="Buscar"
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                fullWidth
              />
              <TextField
                select
                size="small"
                label="Estado"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
                sx={{ minWidth: { md: 180 } }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || "all"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Desde"
                type="date"
                value={filters.date_from}
                onChange={(event) => updateFilter("date_from", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 160 } }}
              />
              <TextField
                size="small"
                label="Hasta"
                type="date"
                value={filters.date_to}
                onChange={(event) => updateFilter("date_to", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 160 } }}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="outlined" onClick={clearFilters} disabled={loading}>
                Limpiar
              </Button>
              <Button variant="contained" onClick={applyFilters} disabled={loading}>
                Filtrar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Bandeja
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Venta</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                          {loading ? "Cargando pendientes..." : "No hay pendientes de facturación."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const status = String(row.status || "").toUpperCase();
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatDateTime(row.created_at)}</TableCell>
                          <TableCell>{row.sale_number || row.sale_id || "-"}</TableCell>
                          <TableCell>{row.customer || "CF"}</TableCell>
                          <TableCell align="right">{toMoney(row.sale_total)}</TableCell>
                          <TableCell>
                            {row.requested_immediate_issue ? "Emisión inmediata" : "Diferida"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={STATUS_LABELS[status] || row.status || "-"}
                              color={STATUS_COLORS[status] || "default"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                status === "PENDING"
                                  ? "Preparar factura interna."
                                  : "Solo aplica para pendientes en estado Pendiente."
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={loading || preparingId === row.id || status !== "PENDING"}
                                  onClick={() => handlePrepareInvoice(row)}
                                >
                                  {preparingId === row.id ? "Preparando..." : "Preparar factura"}
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default InvoicePendingPage;
